/**
 * requestInfo.service.ts
 *
 * The public request-info landing page's two API calls. Raw fetch, no auth
 * client — the page opens without a login, exactly like the eForm renderer
 * (see eform.service.ts), and must never send a staff token.
 *
 * Neither call throws. Each returns a result the page can switch on, because
 * the page's rendering depends on WHY something failed: a throttle (429) is
 * "try again shortly", a validation error names fields, and everything else is
 * a network-style failure. The API answers every "this link is not valid"
 * verdict with the same 200 `{ state: 'neutral' }` body, so a neutral result is
 * data, not an error.
 */

const API_BASE: string = import.meta.env.VITE_API_URL ?? '/api/v5';

/**
 * The SPA route the generated links point at: `https://<host>/l/<code>` for a
 * bare link, `…/l/<code>/<suffix>` once a send has personalized it. A trailing
 * slash with nothing after it (`/l/<code>/`) is a bare link too.
 */
export const REQUEST_INFO_ROUTE_PATH = '/l/:code/:suffix?';

/** The page's own photo (served from public/), used when a link has none of its own. */
export const REQUEST_INFO_DEFAULT_HERO_URL = '/request-info/hero.jpg';

/**
 * The photo band's shape on a phone, where most recipients open the link: the
 * full width of an iPhone (390px) by the band's phone height (176px). Pickers
 * and thumbnails crop to it so a photo is judged as it will be shown.
 */
export const REQUEST_INFO_HERO_ASPECT = '390 / 176';

/** Where the band crops a photo: the default photo was framed for its crop; any other is centred. */
export function heroObjectPosition(url: string | null | undefined): string {
  return url === REQUEST_INFO_DEFAULT_HERO_URL ? '40% 30%' : 'center';
}

/**
 * An author's own description for the page, capped like a resource's (the API's
 * RequestInfoDisplayService::DESCRIPTION_MAX). A page-length choice: the column is TEXT.
 */
export const REQUEST_INFO_DESCRIPTION_MAX = 2000;

/** What the page shows under the button. Absent (an older API build) = everything, the resource's own words. */
export interface RequestInfoDetailsDisplay {
  showResourceImage?: boolean;
  showResourceDescription?: boolean;
  /** Shown in place of the resource's description; '' = the resource's own. */
  customDescription?: string;
}

/**
 * The page's type: Cormorant Garamond headlines over Figtree text. Loaded by the
 * page (and the generator's preview) only, so the rest of the app does not pay
 * for two more families.
 */
export const REQUEST_INFO_FONT_STYLESHEET =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=Figtree:wght@400;500;600&display=swap';

export type RequestInfoResourceKind = 'product' | 'package' | 'appointmentType' | 'membership';

export type RequestInfoState =
  | 'personalized'
  | 'generic'
  | 'preview'
  | 'request-sent'
  | 'deactivated'
  | 'neutral';

const KNOWN_STATES: ReadonlySet<string> = new Set<RequestInfoState>([
  'personalized',
  'generic',
  'preview',
  'request-sent',
  'deactivated',
  'neutral',
]);

export interface RequestInfoResource {
  kind: RequestInfoResourceKind;
  name: string;
  /** Plain text. Never rendered as HTML. */
  description: string;
  price: number | null;
  imageUrl: string | null;
}

export interface RequestInfoBranding {
  logoUrl: string | null;
  lightLogoUrl: string | null;
  buttonColor: string | null;
  backgroundColor: string | null;
}

/**
 * Which account logo sits over the top-left of the page's photo. 'auto' is the
 * light logo, else the dark one — how pages were drawn before the choice existed.
 */
export type RequestInfoLogoVariant = 'auto' | 'none' | 'dark' | 'light';

/** 'auto' made concrete for this account: the light logo, else the dark one, else none. */
export function resolveLogoVariant(
  variant: RequestInfoLogoVariant | null | undefined,
  branding: RequestInfoBranding | null | undefined,
): Exclude<RequestInfoLogoVariant, 'auto'> {
  if (variant === 'none' || variant === 'dark' || variant === 'light') return variant;
  if (branding?.lightLogoUrl) return 'light';
  if (branding?.logoUrl) return 'dark';
  return 'none';
}

/** The logo on the page shown after the tap; 'same' is whatever the page itself shows. */
export type RequestInfoConfirmLogoVariant = 'same' | 'none' | 'dark' | 'light';

/**
 * The photo and logo of the page shown after the tap. Each follows the page's
 * own unless the author picked one for it.
 */
export function confirmationLook(look: {
  heroImageUrl?: string | null;
  logoVariant?: RequestInfoLogoVariant;
  confirmHeroImageUrl?: string | null;
  confirmLogoVariant?: RequestInfoConfirmLogoVariant;
}): { heroImageUrl: string | null; logoVariant: RequestInfoLogoVariant | undefined } {
  const own = look.confirmLogoVariant;
  return {
    heroImageUrl: look.confirmHeroImageUrl || look.heroImageUrl || null,
    logoVariant: own === 'none' || own === 'dark' || own === 'light' ? own : look.logoVariant,
  };
}

/** The logo a choice draws, or null: none chosen, or the account has no logo of that kind. */
export function logoUrlFor(
  variant: RequestInfoLogoVariant | null | undefined,
  branding: RequestInfoBranding | null | undefined,
): string | null {
  switch (resolveLogoVariant(variant, branding)) {
    case 'light':
      return branding?.lightLogoUrl || null;
    case 'dark':
      return branding?.logoUrl || null;
    default:
      return null;
  }
}

export interface RequestInfoFacility {
  name: string;
  phone: string;
}

export interface RequestInfoResolveResponse extends RequestInfoDetailsDisplay {
  state: RequestInfoState;
  /** personalized and request-sent only; null for referral / generic leads. */
  firstName?: string | null;
  resource?: RequestInfoResource;
  /** Plain text; '' when none. */
  note?: string;
  branding?: RequestInfoBranding;
  /** The photo the author chose for the top of the page; null or absent = the default photo. */
  heroImageUrl?: string | null;
  /** Which branding logo sits over the photo; absent = auto. */
  logoVariant?: RequestInfoLogoVariant;
  /** The photo on the page shown after the tap; null or absent = the page's photo. */
  confirmHeroImageUrl?: string | null;
  /** The logo on the page shown after the tap; absent = same as the page. */
  confirmLogoVariant?: RequestInfoConfirmLogoVariant;
  facility?: RequestInfoFacility;
  /** Present for personalized and generic only. The POST must carry it back. */
  actionToken?: string | null;
  /** request-sent only. */
  referral?: boolean;
}

export interface RequestInfoRequestPayload {
  /** '' for a bare link. */
  suffix: string;
  actionToken: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  /** Honeypot. A hidden input no person can see; it must stay empty. */
  website?: string;
}

export interface RequestInfoRequestResponse {
  state: 'request-sent' | 'neutral';
  firstName?: string | null;
  facility?: RequestInfoFacility | null;
  referral?: boolean;
}

export type ResolveResult =
  | { ok: true; data: RequestInfoResolveResponse }
  | { ok: false; reason: 'throttled' | 'network' };

export type SubmitResult =
  | { ok: true; data: RequestInfoRequestResponse }
  | { ok: false; reason: 'validation'; fields: string[]; message: string }
  | { ok: false; reason: 'throttled' | 'network' };

const NEUTRAL: RequestInfoResolveResponse = { state: 'neutral' };

async function readJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const body = await res.json();
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The envelope's `data`, or null when the body is not the shape we expect. */
function envelopeData(body: Record<string, unknown> | null): Record<string, unknown> | null {
  const data = body?.data;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

/**
 * Anything whose state we do not recognise is treated as neutral. Failing closed
 * means a newer API state can never render a half-understood page.
 */
function toResolveResponse(data: Record<string, unknown> | null): RequestInfoResolveResponse {
  if (!data || typeof data.state !== 'string' || !KNOWN_STATES.has(data.state)) return NEUTRAL;
  return data as unknown as RequestInfoResolveResponse;
}

function resolveUrl(code: string, suffix: string): string {
  const base = `${API_BASE}/request-info/${encodeURIComponent(code)}`;
  return suffix ? `${base}/${encodeURIComponent(suffix)}` : base;
}

export const requestInfoService = {
  /**
   * What the page should show for this link. Never writes anything: opening,
   * scanning or previewing a link must not create a lead.
   */
  async resolve(code: string, suffix: string): Promise<ResolveResult> {
    let res: Response;
    try {
      res = await fetch(resolveUrl(code, suffix), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
      });
    } catch {
      return { ok: false, reason: 'network' };
    }
    if (res.status === 429) return { ok: false, reason: 'throttled' };
    if (!res.ok) return { ok: false, reason: 'network' };
    const body = await readJson(res);
    if (!body) return { ok: false, reason: 'network' };
    return { ok: true, data: toResolveResponse(envelopeData(body)) };
  },

  /** The one call that creates a lead: the button's tap, or the visitor form. */
  async request(code: string, payload: RequestInfoRequestPayload): Promise<SubmitResult> {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/request-info/${encodeURIComponent(code)}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(payload),
      });
    } catch {
      return { ok: false, reason: 'network' };
    }

    if (res.status === 429) return { ok: false, reason: 'throttled' };

    const body = await readJson(res);

    if (res.status === 422) {
      // The API names fields only (`error.details.fields`), never echoing what
      // was typed. A Laravel validation 422 arrives as { message, errors }.
      const error = (body?.error ?? null) as
        | { message?: unknown; details?: { fields?: unknown } }
        | null;
      const laravelErrors = body?.errors && typeof body.errors === 'object'
        ? Object.keys(body.errors as Record<string, unknown>)
        : [];
      const fields = Array.isArray(error?.details?.fields)
        ? (error!.details!.fields as unknown[]).map(String)
        : laravelErrors;
      const message = String(error?.message ?? body?.message ?? '');
      return { ok: false, reason: 'validation', fields, message };
    }

    if (!res.ok) return { ok: false, reason: 'network' };

    const data = envelopeData(body);
    if (!data || (data.state !== 'request-sent' && data.state !== 'neutral')) {
      return { ok: false, reason: 'network' };
    }
    return { ok: true, data: data as unknown as RequestInfoRequestResponse };
  },
};
